import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MeasurementsSlide.css';

const ftInToCm = (ft, inches) =>
  Math.round((parseFloat(ft || 0) * 12 + parseFloat(inches || 0)) * 2.54);

const cmToFtIn = (cm) => {
  const totalInches = cm / 2.54;
  const ft = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { ft: String(ft), inches: String(inches) };
};

const lbsToKg = (lbs) => Math.round(parseFloat(lbs) / 2.20462 * 10) / 10;
const kgToLbs = (kg) => Math.round(parseFloat(kg) * 2.20462);

function MeasurementsSlide({ onNext, onBack, initialData }) {
  const { t } = useTranslation('measurements');
  const { t: tc } = useTranslation('common');

  const [unitSystem, setUnitSystem] = useState('metric');
  const [gender, setGender] = useState(initialData.gender || '');
  const [height, setHeight] = useState(initialData.height || '');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [weight, setWeight] = useState(initialData.weight || '');
  const [weightLbs, setWeightLbs] = useState('');
  const [age, setAge] = useState(initialData.age || '');
  const [errors, setErrors] = useState({});

  const handleUnitSwitch = (system) => {
    if (system === unitSystem) return;

    if (system === 'imperial') {
      if (height) {
        const { ft, inches } = cmToFtIn(parseFloat(height));
        setHeightFt(ft);
        setHeightIn(inches);
      }
      if (weight) {
        setWeightLbs(String(kgToLbs(parseFloat(weight))));
      }
    } else {
      if (heightFt || heightIn) {
        setHeight(String(ftInToCm(heightFt, heightIn)));
      }
      if (weightLbs) {
        setWeight(String(lbsToKg(parseFloat(weightLbs))));
      }
    }

    setUnitSystem(system);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!gender) {
      newErrors.gender = t('errors.genderRequired');
    }

    if (unitSystem === 'metric') {
      if (!height || height < 100 || height > 250) {
        newErrors.height = t('errors.heightInvalid');
      }
      if (!weight || weight < 30 || weight > 200) {
        newErrors.weight = t('errors.weightInvalid');
      }
    } else {
      const cmHeight = ftInToCm(heightFt, heightIn);
      if (!heightFt || cmHeight < 100 || cmHeight > 250) {
        newErrors.height = t('errors.heightInvalidImperial');
      }
      if (!weightLbs || weightLbs < 66 || weightLbs > 441) {
        newErrors.weight = t('errors.weightInvalidImperial');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const metricHeight = unitSystem === 'metric'
        ? parseFloat(height)
        : ftInToCm(heightFt, heightIn);
      const metricWeight = unitSystem === 'metric'
        ? parseFloat(weight)
        : lbsToKg(parseFloat(weightLbs));

      onNext({
        gender,
        height: metricHeight,
        weight: metricWeight,
        age: age ? parseInt(age) : null,
      });
    }
  };

  return (
    <div className="slide measurements-slide">
      <div className="slide-content">
        <h2>{t('title')}</h2>
        <p className="slide-description">{t('description')}</p>

        <div className="unit-toggle">
          <button
            type="button"
            className={`unit-toggle-btn ${unitSystem === 'metric' ? 'active' : ''}`}
            onClick={() => handleUnitSwitch('metric')}
          >
            cm / kg
          </button>
          <button
            type="button"
            className={`unit-toggle-btn ${unitSystem === 'imperial' ? 'active' : ''}`}
            onClick={() => handleUnitSwitch('imperial')}
          >
            ft / lbs
          </button>
        </div>

        <form className="measurement-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('form.genderLabel')}</label>
            <div className="gender-buttons">
              <button
                type="button"
                className={`gender-btn ${gender === 'homme' ? 'active' : ''}`}
                onClick={() => setGender('homme')}
              >
                {tc('gender.male')}
              </button>
              <button
                type="button"
                className={`gender-btn ${gender === 'femme' ? 'active' : ''}`}
                onClick={() => setGender('femme')}
              >
                {tc('gender.female')}
              </button>
            </div>
            {errors.gender && <span className="error-message">{errors.gender}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">{unitSystem === 'metric' ? t('form.heightLabel') : t('form.heightLabelImperial')}</label>
            {unitSystem === 'metric' ? (
              <div className="input-with-unit">
                <input
                  type="number"
                  id="height"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  min="100"
                  max="250"
                  placeholder={t('form.heightPlaceholder')}
                  className={`form-input ${errors.height ? 'error' : ''}`}
                />
                <span className="unit">{tc('units.cm')}</span>
              </div>
            ) : (
              <div className="input-imperial-height">
                <div className="input-with-unit">
                  <input
                    type="number"
                    value={heightFt}
                    onChange={(e) => setHeightFt(e.target.value)}
                    min="3"
                    max="8"
                    placeholder={t('form.heightFeetPlaceholder')}
                    className={`form-input ${errors.height ? 'error' : ''}`}
                  />
                  <span className="unit">{tc('units.ft')}</span>
                </div>
                <div className="input-with-unit">
                  <input
                    type="number"
                    value={heightIn}
                    onChange={(e) => setHeightIn(e.target.value)}
                    min="0"
                    max="11"
                    placeholder={t('form.heightInchesPlaceholder')}
                    className={`form-input ${errors.height ? 'error' : ''}`}
                  />
                  <span className="unit">{tc('units.in')}</span>
                </div>
              </div>
            )}
            {errors.height && <span className="error-message">{errors.height}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="weight" className="form-label">{unitSystem === 'metric' ? t('form.weightLabel') : t('form.weightLabelImperial')}</label>
            <div className="input-with-unit">
              {unitSystem === 'metric' ? (
                <>
                  <input
                    type="number"
                    id="weight"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    min="30"
                    max="200"
                    placeholder={t('form.weightPlaceholder')}
                    className={`form-input ${errors.weight ? 'error' : ''}`}
                  />
                  <span className="unit">{tc('units.kg')}</span>
                </>
              ) : (
                <>
                  <input
                    type="number"
                    id="weight"
                    value={weightLbs}
                    onChange={(e) => setWeightLbs(e.target.value)}
                    min="66"
                    max="441"
                    placeholder={t('form.weightPlaceholderImperial')}
                    className={`form-input ${errors.weight ? 'error' : ''}`}
                  />
                  <span className="unit">{tc('units.lbs')}</span>
                </>
              )}
            </div>
            {errors.weight && <span className="error-message">{errors.weight}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="age" className="form-label">{t('form.ageLabel')}</label>
            <div className="input-with-unit">
              <input
                type="number"
                id="age"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                min="15"
                max="90"
                placeholder={t('form.agePlaceholder')}
                className="form-input"
              />
              <span className="unit">{t('form.ageUnit')}</span>
            </div>
          </div>
        </form>

        <div className="navigation-buttons">
          {onBack && (
            <button className="btn btn-secondary" onClick={onBack}>
              {tc('buttons.back')}
            </button>
          )}
          <button className="btn btn-primary" onClick={handleSubmit}>
            {tc('buttons.next')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MeasurementsSlide;
